import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["element"]
  static values = {
    speed: { type: Number, default: 0.5 }
  }

  connect() {
    // レイヤークラスと対応する速度の定義
    this.layerSpeeds = {
      "layer-1": 1,
      "layer-2": 0.3,
      "layer-3": 0.5,
      "layer-4": 0.2
    }

    // 各セクションの初期表示位置を記録
    this.sectionInitPositions = {}

    // 各セクションの表示状態を記録
    this.sectionVisibility = {}

    // requestAnimationFrameを使用してパフォーマンスを最適化
    this.rafId = null
    this.scrollY = window.scrollY
    this.scrollHandler = this._handleScroll.bind(this)
    this.updateHandler = this._update.bind(this)

    // クラスベースの要素を見つける
    this.classBasedElements = {}
    Object.keys(this.layerSpeeds).forEach(className => {
      this.classBasedElements[className] = Array.from(this.element.querySelectorAll(`.${className}`))
    })

    // セクションの要素を取得
    this.sections = Array.from(this.element.querySelectorAll('#recruit, #about, #message'))

    // Intersection Observerを設定
    this.setupIntersectionObserver()

    window.addEventListener("scroll", this.scrollHandler, { passive: true })

    // 初期状態を設定
    this._update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler)
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
    }
    // Intersection Observerを解除
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupIntersectionObserver() {
    // Intersection Observerのオプション設定
    const options = {
      root: null, // ビューポート全体を対象
      rootMargin: '0px',
      threshold: 0.1 // 要素が10%見えたら発火
    }

    // Intersection Observerの作成
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const sectionId = entry.target.id

        if (entry.isIntersecting) {
          // 要素が表示された
          this.sectionVisibility[sectionId] = true

          // 要素が初めて表示された場合、現在のスクロール位置を記録
          if (!this.sectionInitPositions[sectionId]) {
            this.sectionInitPositions[sectionId] = window.scrollY
          }
        } else {
          // 要素が非表示になった
          this.sectionVisibility[sectionId] = false
        }
      })
    }, options)

    // 対象セクションの監視を開始
    this.sections.forEach(section => {
      this.observer.observe(section)
    })
  }

  _handleScroll() {
    // スクロール位置を保存するだけ（頻繁な再計算を避ける）
    this.scrollY = window.scrollY

    // まだrequestAnimationFrameが実行中でなければ更新をスケジュール
    if (!this.rafId) {
      this.rafId = requestAnimationFrame(this.updateHandler)
    }
  }

  _update() {
    this.rafId = null

    // 各セクション内のパララックス要素を処理
    this.sections.forEach(section => {
      const sectionId = section.id

      // セクションが表示されていない場合は処理しない
      if (!this.sectionVisibility[sectionId]) return

      // セクションが初めて表示された位置を取得
      const initialPosition = this.sectionInitPositions[sectionId] || 0

      // 現在位置と初期表示位置の差分だけを使用してパララックス効果を計算
      const effectiveScrollY = this.scrollY - initialPosition

      // このセクション内のレイヤー要素を処理
      Object.entries(this.classBasedElements).forEach(([className, elements]) => {
        const speed = this.layerSpeeds[className]

        elements.forEach(element => {
          // この要素がこのセクションに属しているか確認
          if (section.contains(element)) {
            // 差分スクロール量に基づいて位置を計算
            // 負の値にならないようにする（表示前の要素が上に移動しすぎることを防止）
            const yPos = Math.max(-(effectiveScrollY * speed), -500)
            element.style.transform = `translate3d(0, ${yPos}px, 0)`
          }
        })
      })
    })

    // 1. 通常のelementターゲット（明示的な速度指定あり）- セクション外の要素用
    this.elementTargets.forEach(element => {
      // この要素がどのセクションにも属していない場合のみ処理
      if (!this.sections.some(section => section.contains(element))) {
        const speed = element.dataset.parallaxSpeedValue || this.speedValue
        const yPos = -(this.scrollY * speed)
        element.style.transform = `translate3d(0, ${yPos}px, 0)`
      }
    })
  }
}